import { ContractBonus } from "@/types/bonuses";
import { Commodity, Contract } from "@/types/contract";
import { Product } from "@/types/enumerations";
import { buildAccountNumber } from "@/utils/payment";
import { getCurrentProductName } from "./product";

const getConsumptionFirstLevel = (yearConsumptionUnits: string, yearConsumption: number) => {
  return (
    (yearConsumptionUnits === "mwh" && yearConsumption <= 1.59) ||
    (yearConsumptionUnits === "kwh" && yearConsumption <= 1590) ||
    (yearConsumptionUnits === "m3" && yearConsumption <= 150)
  );
};

const getConsumptionSecondLevel = (yearConsumptionUnits: string, yearConsumption: number) => {
  return (
    (yearConsumptionUnits === "mwh" && yearConsumption <= 2.99) ||
    (yearConsumptionUnits === "kwh" && yearConsumption <= 2990) ||
    (yearConsumptionUnits === "m3" && yearConsumption <= 283)
  );
};

const getConsumptionThirdLevel = (yearConsumptionUnits: string, yearConsumption: number) => {
  return (
    (yearConsumptionUnits === "mwh" && yearConsumption <= 7.49) ||
    (yearConsumptionUnits === "kwh" && yearConsumption <= 7490) ||
    (yearConsumptionUnits === "m3" && yearConsumption <= 711)
  );
};

const getGasBonusKomplet = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
    return 1000;
  } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 1500;
  } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
    return 2500;
  } else {
    return 7500;
  }
};

const getGasBonusAffordable = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
    return 1500;
  } else {
    return 2000;
  }
};

const getGasBonusVariantOnline = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else {
    return 1500;
  }
};

const getGasBonusVariant = (yearConsumption: number, yearConsumptionUnits: string, product: Product) => {
  if (
    product.value.pricelistValidMonth === 12 ||
    product.value.pricelistValidMonth === 11 ||
    product.value.pricelistValidMonth === 0
  ) {
    if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
      return 0;
    } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
      return 500;
    } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
      return 1000;
    } else {
      return 2500;
    }
  } else {
    if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
      return 500;
    } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
      return 1000;
    } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
      return 2500;
    } else {
      return 4500;
    }
  }
};

const getGasBonus = (
  contract: Contract,
  yearConsumption: number | null,
  yearConsumptionUnits: string | null,
  products: Product[]
) => {
  const product = products.find(product => product.id === contract.contractDetails!.productId);

  if (!yearConsumption || !product || !yearConsumptionUnits) {
    return 1000;
  }

  const productName = getCurrentProductName(contract, products);
  const isKompletProduct: boolean = productName.toLowerCase().includes("komplet");
  const isOnlineProduct: boolean = productName.toLowerCase().includes("online");
  const isAffordableProduct = productName.toLowerCase().includes("výhodně");

  if (isAffordableProduct) {
    return getGasBonusAffordable(yearConsumption, yearConsumptionUnits);
  }
  if (isKompletProduct) {
    return getGasBonusKomplet(yearConsumption, yearConsumptionUnits);
  } else if (isOnlineProduct) {
    return getGasBonusVariantOnline(yearConsumption, yearConsumptionUnits);
  } else {
    return getGasBonusVariant(yearConsumption, yearConsumptionUnits, product);
  }
};

const getEleBonusKomplet = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
    return 1000;
  } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 1500;
  } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
    return 4000;
  } else {
    return 4500;
  }
};

const getEleBonusAffordable = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
    return 2000;
  } else {
    return 2000;
  }
};

const getEleBonusVariantOnline = (yearConsumption: number, yearConsumptionUnits: string) => {
  if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
    return 500;
  } else {
    return 2000;
  }
};

const getEleBonusVariant = (yearConsumption: number, yearConsumptionUnits: string, product: Product) => {
  if (
    product.value.pricelistValidMonth === 12 ||
    product.value.pricelistValidMonth === 11 ||
    product.value.pricelistValidMonth === 0
  ) {
    if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
      return 0;
    } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
      return 500;
    } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
      return 1000;
    } else {
      return 3000;
    }
  } else {
    if (getConsumptionFirstLevel(yearConsumptionUnits, yearConsumption)) {
      return 500;
    } else if (getConsumptionSecondLevel(yearConsumptionUnits, yearConsumption)) {
      return 1000;
    } else if (getConsumptionThirdLevel(yearConsumptionUnits, yearConsumption)) {
      return 3000;
    } else {
      return 4000;
    }
  }
};

const getEleBonus = (
  contract: Contract,
  yearConsumption: number | null,
  yearConsumptionUnits: string | null,
  products: Product[]
) => {
  const product = products.find(product => product.id === contract.contractDetails!.productId);

  if (!yearConsumption || !product || !yearConsumptionUnits) {
    return 1000;
  }

  const productName = getCurrentProductName(contract, products);
  const isKompletProduct: boolean = productName.toLowerCase().includes("komplet");
  const isOnlineProduct: boolean = productName.toLowerCase().includes("online");
  const isAffordableProduct = productName.toLowerCase().includes("výhodně");

  if (isAffordableProduct) {
    return getEleBonusAffordable(yearConsumption, yearConsumptionUnits);
  }
  if (isKompletProduct) {
    return getEleBonusKomplet(yearConsumption, yearConsumptionUnits);
  }
  if (isOnlineProduct) {
    return getEleBonusVariantOnline(yearConsumption, yearConsumptionUnits);
  } else {
    return getEleBonusVariant(yearConsumption, yearConsumptionUnits, product);
  }
};

export const getBonus = (
  contract: Contract,
  yearConsumption: number | null,
  yearConsumptionUnits: string | null,
  products: Product[]
): number => {
  if (contract.supplyPoint.commodity === Commodity.Electricity) {
    return getEleBonus(contract, yearConsumption, yearConsumptionUnits, products);
  } else {
    return getGasBonus(contract, yearConsumption, yearConsumptionUnits, products);
  }
};

export const getBonusMappedText = (text: string, bonus: ContractBonus) => {
  return text
    .replace("<BONUS_AMOUNT>", `${bonus.amount}`)
    .replace(
      "<BONUS_ACCOUNT_NUMBER_FULL>",
      buildAccountNumber(bonus.accountNumberPrefix, bonus.accountNumber, bonus.accountNumberBankId)
    )
    .replace("<BONUS_MATURITY>", `${bonus.maturity}`)
    .replace("<BONUS_DURATION>", `${bonus.duration}`)
    .replace("<BONUS_ACCOUNT_NUMBER_PREFIX>", bonus.accountNumberPrefix ?? "")
    .replace("<BONUS_ACCOUNT_NUMBER>", bonus.accountNumber ?? "")
    .replace("<BONUS_ACCOUNT_BANK_ID>", bonus.accountNumberBankId ?? "");
};
